/**
 * NR Photo Booth v2
 * Split UI + MediaPipe iris masks + local snaps.
 */
(function () {
  'use strict';

  var MEDIAPIPE_VISION_CDN =
    'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18';
  var FACE_MODEL_URL =
    'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

  var LEFT_IRIS = {
    center: 473,
    ring: [474, 475, 476, 477],
    eyelids: [263, 249, 390, 373, 374, 380, 381, 382, 362, 398, 384, 385, 386, 387, 388, 466]
  };
  var RIGHT_IRIS = {
    center: 468,
    ring: [469, 470, 471, 472],
    eyelids: [33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160, 161, 246]
  };

  var PLACEHOLDER_LOGO =
    'https://cdn.shopify.com/s/files/1/0795/7748/1401/files/nr_eye_logo.png?v=1785382455';

  var CONSTRAINTS = {
    audio: false,
    video: {
      facingMode: 'user',
      width: { ideal: 640 },
      height: { ideal: 480 }
    }
  };

  var DETECT_EVERY_MS = 66;
  var MAX_SNAPS = 4;
  var POST_LABEL = 'post to photobooth gallery';

  function trackBooth(button) {
    if (typeof posthog === 'undefined' || typeof posthog.capture !== 'function') return;
    posthog.capture('photobooth_v2_click', { button: button });
  }

  function boothButtonName(el) {
    if (!el) return '';
    if (el.hasAttribute('data-nr-booth-v2-capture')) return 'take_photo';
    if (el.hasAttribute('data-nr-booth-v2-download')) return 'download_pics';
    if (el.hasAttribute('data-nr-booth-v2-post')) return 'post_gallery';
    if (el.hasAttribute('data-nr-booth-v2-more')) return 'take_more';
    if (el.hasAttribute('data-nr-booth-v2-enter')) {
      return el.classList.contains('nr-photo-booth-v2__plaque')
        ? 'nothing_photobooth'
        : 'enter';
    }
    return '';
  }

  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.onload = function () {
        resolve(img);
      };
      img.onerror = reject;
      img.src = src;
    });
  }

  function drawCover(ctx, img, x, y, w, h) {
    var iw = img.naturalWidth || img.width;
    var ih = img.naturalHeight || img.height;
    if (!iw || !ih) return;
    var scale = Math.max(w / iw, h / ih);
    var dw = iw * scale;
    var dh = ih * scale;
    var dx = x + (w - dw) / 2;
    var dy = y + (h - dh) / 2;
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    ctx.drawImage(img, dx, dy, dw, dh);
    ctx.restore();
  }

  function createBooth(root) {
    var gif = root.querySelector('[data-nr-booth-v2-gif]');
    var video = root.querySelector('[data-nr-booth-v2-video]');
    var canvas = root.querySelector('[data-nr-booth-v2-canvas]');
    var ctx = canvas ? canvas.getContext('2d') : null;
    var flash = root.querySelector('[data-nr-booth-v2-flash]');
    var idle = root.querySelector('[data-nr-booth-v2-idle]');
    var live = root.querySelector('[data-nr-booth-v2-live]');
    var enterBtns = root.querySelectorAll('[data-nr-booth-v2-enter]');
    var captureBtn = root.querySelector('[data-nr-booth-v2-capture]');
    var errorEl = root.querySelector('[data-nr-booth-v2-error]');
    var gallery = root.querySelector('[data-nr-booth-v2-gallery]');
    var doneEl = root.querySelector('[data-nr-booth-v2-done]');
    var downloadBtn = root.querySelector('[data-nr-booth-v2-download]');
    var postBtn = root.querySelector('[data-nr-booth-v2-post]');
    var moreBtn = root.querySelector('[data-nr-booth-v2-more]');
    var intro = root.querySelector('[data-nr-booth-v2-intro]');
    var mediaPane = root.querySelector('.nr-photo-booth-v2__pane--media');
    var shutterTakingTimer = 0;
    var snaps = [];
    var posting = false;

    var logoUrl = root.getAttribute('data-logo-url') || '';
    var galleryApi = (root.getAttribute('data-gallery-api') || '').replace(/\/$/, '');
    var galleryFolder = root.getAttribute('data-gallery-folder') || 'nr-photo-booth';
    var logoScale = (parseFloat(root.getAttribute('data-logo-scale')) || 70) / 100;
    var logoImage = new Image();
    logoImage.decoding = 'async';
    logoImage.crossOrigin = 'anonymous';
    logoImage.onerror = function () {
      logoImage.onerror = null;
      logoImage.removeAttribute('crossorigin');
      logoImage.src = PLACEHOLDER_LOGO;
    };
    logoImage.src = logoUrl || PLACEHOLDER_LOGO;

    var stream = null;
    var faceLandmarker = null;
    var rafId = 0;
    var lastDetectMs = 0;
    var lastLandmarks = null;
    var running = false;
    var starting = false;
    var detecting = false;
    var detectFailed = false;
    var useImageMode = false;
    var glCanvas = null;
    var detectCanvas = document.createElement('canvas');
    var detectCtx = detectCanvas.getContext('2d', { willReadFrequently: true });

    function showError(message) {
      if (!errorEl) return;
      if (!message) {
        errorEl.hidden = true;
        errorEl.textContent = '';
        return;
      }
      errorEl.hidden = false;
      errorEl.textContent = message;
    }

    function landmarkToCanvas(landmark, layout) {
      var vx = landmark.x * layout.vw;
      var vy = landmark.y * layout.vh;
      var x = (vx - layout.sx) * (layout.w / layout.sw);
      var y = (vy - layout.sy) * (layout.h / layout.sh);
      return { x: layout.w - x, y: y };
    }

    function coverLayout() {
      if (!canvas || !video) return null;
      var vw = video.videoWidth;
      var vh = video.videoHeight;
      if (!vw || !vh) return null;

      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var box = (mediaPane && mediaPane.getBoundingClientRect()) || canvas.getBoundingClientRect();
      var cssW = Math.round(box.width) || canvas.clientWidth || 0;
      var cssH = Math.round(box.height) || canvas.clientHeight || 0;
      var w = Math.max(1, Math.round(cssW * dpr));
      var h = Math.max(1, Math.round(cssH * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }

      var scale = Math.max(w / vw, h / vh);
      var sw = w / scale;
      var sh = h / scale;
      var sx = (vw - sw) / 2;
      var sy = (vh - sh) / 2;
      return { w: w, h: h, vw: vw, vh: vh, sx: sx, sy: sy, sw: sw, sh: sh };
    }

    function irisMetrics(landmarks, iris, layout) {
      var c = landmarks[iris.center];
      if (!c) return null;
      var center = landmarkToCanvas(c, layout);
      var sum = 0;
      var count = 0;
      iris.ring.forEach(function (idx) {
        var p = landmarks[idx];
        if (!p) return;
        var pt = landmarkToCanvas(p, layout);
        sum += Math.hypot(pt.x - center.x, pt.y - center.y);
        count += 1;
      });
      if (!count) return null;
      return { x: center.x, y: center.y, radius: sum / count };
    }

    function eyelidPoints(landmarks, iris, layout, center) {
      var points = [];
      var minY = Infinity;
      var maxY = -Infinity;
      iris.eyelids.forEach(function (idx) {
        var lm = landmarks[idx];
        if (!lm) return;
        var p = landmarkToCanvas(lm, layout);
        var x = p.x + (center.x - p.x) * 0.12;
        var y = p.y + (center.y - p.y) * 0.12;
        points.push({ x: x, y: y });
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      });
      return {
        points: points,
        openingHeight: maxY - minY
      };
    }

    function clipToEyelids(points) {
      if (!ctx || !points.length) return false;
      ctx.beginPath();
      points.forEach(function (p, i) {
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.closePath();
      ctx.clip();
      return true;
    }

    function drawLogoAt(x, y, diameter) {
      if (!ctx || !logoImage.complete || !logoImage.naturalWidth) return;
      var size = Math.max(4, diameter * logoScale);
      ctx.save();
      ctx.beginPath();
      ctx.arc(x, y, size / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(logoImage, x - size / 2, y - size / 2, size, size);
      ctx.restore();
    }

    function drawEyeMask(landmarks, iris, layout) {
      var m = irisMetrics(landmarks, iris, layout);
      if (!m) return;

      var lids = eyelidPoints(landmarks, iris, layout, m);
      if (!lids.points.length || lids.openingHeight < m.radius * 0.35) return;

      var maskRadius = m.radius * 1.08;
      if (lids.openingHeight > 0) {
        maskRadius = Math.min(maskRadius, lids.openingHeight * 0.72);
      }

      ctx.save();
      if (!clipToEyelids(lids.points)) {
        ctx.restore();
        return;
      }

      ctx.beginPath();
      ctx.arc(m.x, m.y, maskRadius, 0, Math.PI * 2);
      ctx.fillStyle = '#181a1d';
      ctx.fill();

      drawLogoAt(m.x, m.y, maskRadius * 2);
      ctx.restore();
    }

    function drawComposite(landmarksList) {
      var layout = coverLayout();
      if (!layout || !ctx) return;

      ctx.clearRect(0, 0, layout.w, layout.h);
      if (!landmarksList || !landmarksList.length) return;

      landmarksList.forEach(function (landmarks) {
        drawEyeMask(landmarks, LEFT_IRIS, layout);
        drawEyeMask(landmarks, RIGHT_IRIS, layout);
      });
    }

    function bootstrapWebGL() {
      if (glCanvas && glCanvas.parentNode) return;
      glCanvas = document.createElement('canvas');
      glCanvas.width = 8;
      glCanvas.height = 8;
      glCanvas.setAttribute('aria-hidden', 'true');
      glCanvas.style.cssText =
        'position:absolute;width:8px;height:8px;opacity:0;pointer-events:none;bottom:0;left:0;';
      (mediaPane || root).appendChild(glCanvas);
      var gl =
        glCanvas.getContext('webgl2', { antialias: false, depth: false }) ||
        glCanvas.getContext('webgl', { antialias: false, depth: false }) ||
        glCanvas.getContext('experimental-webgl', { antialias: false, depth: false });
      if (!gl) {
        console.warn('[nr-photo-booth-v2] WebGL context bootstrap failed');
      }
    }

    function grabImageData() {
      if (!detectCtx || !video.videoWidth || !video.videoHeight) return null;
      var vw = video.videoWidth;
      var vh = video.videoHeight;
      var maxW = 480;
      var scale = vw > maxW ? maxW / vw : 1;
      var w = Math.max(1, Math.round(vw * scale));
      var h = Math.max(1, Math.round(vh * scale));
      if (detectCanvas.width !== w) detectCanvas.width = w;
      if (detectCanvas.height !== h) detectCanvas.height = h;
      detectCtx.drawImage(video, 0, 0, w, h);
      return detectCtx.getImageData(0, 0, w, h);
    }

    function switchToImageMode() {
      if (!faceLandmarker || useImageMode) return;
      try {
        if (typeof faceLandmarker.setOptions === 'function') {
          faceLandmarker.setOptions({ runningMode: 'IMAGE' });
        }
        useImageMode = true;
      } catch (err) {
        console.warn('[nr-photo-booth-v2] setOptions IMAGE failed', err);
        detectFailed = true;
      }
    }

    function runDetect(now) {
      if (!faceLandmarker) return;
      var result;
      if (useImageMode) {
        var imageData = grabImageData();
        if (!imageData) return;
        result = faceLandmarker.detect(imageData);
      } else {
        result = faceLandmarker.detectForVideo(video, now);
      }
      lastLandmarks = (result && result.faceLandmarks) || null;
    }

    function loop() {
      if (!running) return;
      rafId = requestAnimationFrame(loop);
      if (video.readyState < 2 || !video.videoWidth) return;

      var now = performance.now();
      if (
        faceLandmarker &&
        !detectFailed &&
        !detecting &&
        now - lastDetectMs >= DETECT_EVERY_MS
      ) {
        lastDetectMs = now;
        detecting = true;
        try {
          runDetect(now);
        } catch (err) {
          console.warn('[nr-photo-booth-v2] detectForVideo failed', err);
          if (!useImageMode) {
            switchToImageMode();
            try {
              runDetect(now);
            } catch (err2) {
              detectFailed = true;
              console.warn('[nr-photo-booth-v2] ImageData detect failed', err2);
            }
          } else {
            detectFailed = true;
          }
        }
        detecting = false;
      }

      drawComposite(lastLandmarks);
    }

    async function importVision() {
      try {
        return await import(MEDIAPIPE_VISION_CDN + '/vision_bundle.mjs');
      } catch (err) {
        console.warn('[nr-photo-booth-v2] vision_bundle import failed, retrying package root', err);
        return await import(MEDIAPIPE_VISION_CDN);
      }
    }

    async function loadFaceLandmarker() {
      bootstrapWebGL();
      var vision = await importVision();
      var FaceLandmarker = vision.FaceLandmarker;
      var FilesetResolver = vision.FilesetResolver;
      if (!FaceLandmarker || !FilesetResolver) {
        throw new Error('MediaPipe FaceLandmarker exports missing');
      }

      var fileset = await FilesetResolver.forVisionTasks(MEDIAPIPE_VISION_CDN + '/wasm');
      // Same as v1: CPU inference, VIDEO mode, default wasm (frame upload still uses WebGL).
      return FaceLandmarker.createFromOptions(fileset, {
        baseOptions: {
          modelAssetPath: FACE_MODEL_URL,
          delegate: 'CPU'
        },
        runningMode: 'VIDEO',
        numFaces: 1,
        outputFaceBlendshapes: false,
        outputFacialTransformationMatrixes: false
      });
    }

    function nextSlot() {
      if (!gallery || snaps.length >= MAX_SNAPS) return null;
      return gallery.querySelector('[data-nr-booth-v2-slot]:not(.is-filled)');
    }

    function showDone() {
      if (captureBtn) captureBtn.hidden = true;
      if (doneEl) doneEl.hidden = false;
    }

    function showShutter() {
      if (doneEl) doneEl.hidden = true;
      if (captureBtn) captureBtn.hidden = false;
    }

    function resetSlots() {
      snaps = [];
      posting = false;
      if (postBtn) {
        postBtn.disabled = !galleryApi;
        postBtn.textContent = POST_LABEL;
      }
      if (!gallery) return;
      gallery.querySelectorAll('[data-nr-booth-v2-slot]').forEach(function (slot) {
        slot.classList.remove('is-filled');
        slot.replaceChildren();
      });
    }

    function takeMore() {
      showError('');
      resetSlots();
      showShutter();
    }

    async function buildCollageBlob() {
      if (snaps.length < MAX_SNAPS) return null;
      var imgs = await Promise.all(snaps.map(loadImage));
      var cellW = 480;
      var cellH = 640;
      var gap = 16;
      var out = document.createElement('canvas');
      out.width = cellW * 2 + gap * 3;
      out.height = cellH * 2 + gap * 3;
      var octx = out.getContext('2d');
      if (!octx) return null;
      octx.fillStyle = '#111';
      octx.fillRect(0, 0, out.width, out.height);
      imgs.forEach(function (img, i) {
        var col = i % 2;
        var row = Math.floor(i / 2);
        drawCover(
          octx,
          img,
          gap + col * (cellW + gap),
          gap + row * (cellH + gap),
          cellW,
          cellH
        );
      });
      return new Promise(function (resolve) {
        out.toBlob(
          function (blob) {
            resolve(blob);
          },
          'image/jpeg',
          0.92
        );
      });
    }

    async function downloadPics() {
      showError('');
      try {
        var blob = await buildCollageBlob();
        if (!blob) return;
        var file = new File([blob], 'nothing-photobooth.jpg', { type: 'image/jpeg' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({ files: [file], title: 'nothing photobooth' });
            return;
          } catch (err) {
            if (err && err.name === 'AbortError') return;
          }
        }
        var url = URL.createObjectURL(blob);
        var link = document.createElement('a');
        link.href = url;
        link.download = 'nothing-photobooth.jpg';
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        console.warn('[nr-photo-booth-v2] download failed', err);
        showError('Could not save pics. Try again.');
      }
    }

    async function postCollage() {
      if (!galleryApi || posting) return;
      posting = true;
      if (postBtn) {
        postBtn.disabled = true;
        postBtn.textContent = 'Posting…';
      }
      showError('');

      try {
        var blob = await buildCollageBlob();
        if (!blob) throw new Error('No collage');

        var signRes = await fetch(
          galleryApi + '/api/sign?folder=' + encodeURIComponent(galleryFolder)
        );
        if (!signRes.ok) throw new Error('Could not get upload signature');
        var sign = await signRes.json();
        if (!sign.signature || !sign.cloudName || !sign.apiKey) {
          throw new Error('Invalid upload signature');
        }

        var folder = sign.folder || galleryFolder;
        var formData = new FormData();
        formData.append('file', blob, 'booth.jpg');
        formData.append('api_key', sign.apiKey);
        formData.append('timestamp', String(sign.timestamp));
        formData.append('signature', sign.signature);
        formData.append('folder', folder);

        var uploadRes = await fetch(
          'https://api.cloudinary.com/v1_1/' + sign.cloudName + '/image/upload',
          { method: 'POST', body: formData }
        );
        if (!uploadRes.ok) throw new Error('Upload failed');

        if (postBtn) postBtn.textContent = 'Posted';
      } catch (err) {
        posting = false;
        if (postBtn) {
          postBtn.disabled = false;
          postBtn.textContent = POST_LABEL;
        }
        console.warn('[nr-photo-booth-v2] post failed', err);
        showError('Could not post photo. Check your connection and try again.');
      }
    }

    function pulseShutter() {
      if (!captureBtn) return;
      if (shutterTakingTimer) clearTimeout(shutterTakingTimer);
      captureBtn.classList.remove('is-taking');
      void captureBtn.offsetWidth;
      captureBtn.classList.add('is-taking');
      shutterTakingTimer = setTimeout(function () {
        captureBtn.classList.remove('is-taking');
        shutterTakingTimer = 0;
      }, 140);
    }

    function capture() {
      if (!running || snaps.length >= MAX_SNAPS) return;
      var layout = coverLayout();
      if (!layout || !ctx) return;

      drawComposite(lastLandmarks);

      var out = document.createElement('canvas');
      out.width = layout.w;
      out.height = layout.h;
      var octx = out.getContext('2d');
      if (!octx) return;

      octx.save();
      octx.translate(layout.w, 0);
      octx.scale(-1, 1);
      octx.drawImage(
        video,
        layout.sx,
        layout.sy,
        layout.sw,
        layout.sh,
        0,
        0,
        layout.w,
        layout.h
      );
      octx.restore();
      octx.drawImage(canvas, 0, 0);

      var slot = nextSlot();
      if (!slot) return;
      var dataUrl = out.toDataURL('image/jpeg', 0.92);
      snaps.push(dataUrl);
      var img = document.createElement('img');
      img.src = dataUrl;
      img.alt = 'Your snap';
      slot.replaceChildren(img);
      slot.classList.add('is-filled');

      if (flash) {
        flash.classList.remove('is-active');
        void flash.offsetWidth;
        flash.classList.add('is-active');
      }

      if (snaps.length >= MAX_SNAPS) showDone();
    }

    function stop() {
      running = false;
      if (shutterTakingTimer) {
        clearTimeout(shutterTakingTimer);
        shutterTakingTimer = 0;
      }
      if (captureBtn) captureBtn.classList.remove('is-taking');
      if (rafId) cancelAnimationFrame(rafId);
      rafId = 0;
      lastLandmarks = null;
      lastDetectMs = 0;
      detectFailed = false;
      detecting = false;
      useImageMode = false;
      if (stream) {
        stream.getTracks().forEach(function (track) {
          track.stop();
        });
        stream = null;
      }
      if (faceLandmarker && typeof faceLandmarker.close === 'function') {
        try {
          faceLandmarker.close();
        } catch (_) {}
      }
      faceLandmarker = null;
      if (glCanvas && glCanvas.parentNode) {
        glCanvas.parentNode.removeChild(glCanvas);
      }
      glCanvas = null;
      if (video) {
        video.srcObject = null;
        video.hidden = true;
      }
      if (ctx && canvas) ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (gif) gif.hidden = false;
      if (idle) idle.hidden = false;
      if (live) live.hidden = true;
      hideIntro();
      resetSlots();
      showShutter();
    }

    function hideIntro() {
      if (!intro) return;
      intro.hidden = true;
      intro.classList.remove('is-on', 'is-pop', 'is-snap', 'is-out');
    }

    function wait(ms) {
      return new Promise(function (resolve) {
        setTimeout(resolve, ms);
      });
    }

    async function playEnterIntro() {
      if (!intro) return;
      intro.hidden = false;
      intro.classList.remove('is-on', 'is-pop', 'is-snap', 'is-out');
      void intro.offsetWidth;
      intro.classList.add('is-on');
      await wait(50);
      intro.classList.add('is-pop');
      await wait(420);
      intro.classList.add('is-snap');
      await wait(280);
    }

    async function finishEnterIntro() {
      if (!intro) return;
      intro.classList.add('is-out');
      await wait(260);
      hideIntro();
    }

    async function enter() {
      if (starting || running) return;
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        showError('Camera is not available in this browser.');
        return;
      }
      starting = true;
      showError('');
      if (idle) idle.hidden = true;
      var introPromise = playEnterIntro();
      try {
        stream = await navigator.mediaDevices.getUserMedia(CONSTRAINTS);
        video.srcObject = stream;
        video.playsInline = true;
        video.muted = true;
        video.hidden = false;
        gif.hidden = true;
        await video.play().catch(function () {});

        try {
          faceLandmarker = await loadFaceLandmarker();
          showError('');
        } catch (err) {
          console.warn('[nr-photo-booth-v2] Face Landmarker failed to load', err);
          showError('Face tracking could not load. Live camera still works.');
        }

        await introPromise;
        live.hidden = false;
        await finishEnterIntro();

        lastLandmarks = null;
        lastDetectMs = 0;
        detectFailed = false;
        detecting = false;
        useImageMode = false;
        resetSlots();
        showShutter();
        if (postBtn) postBtn.disabled = !galleryApi;
        running = true;
        loop();
      } catch (err) {
        var denied = err && (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError');
        showError(
          denied
            ? 'Camera permission was denied. Allow access to enter the photobooth.'
            : 'Could not start the camera. Try again.'
        );
        hideIntro();
        if (gif) gif.hidden = false;
        if (idle) idle.hidden = false;
        if (live) live.hidden = true;
        if (video) video.hidden = true;
      } finally {
        starting = false;
      }
    }

    enterBtns.forEach(function (btn) {
      btn.addEventListener('click', enter);
    });
    if (captureBtn) {
      captureBtn.addEventListener('pointerdown', pulseShutter);
      captureBtn.addEventListener('click', capture);
    }
    if (downloadBtn) downloadBtn.addEventListener('click', downloadPics);
    if (postBtn) postBtn.addEventListener('click', postCollage);
    if (moreBtn) moreBtn.addEventListener('click', takeMore);
    root.addEventListener('click', function (event) {
      var target = event.target && event.target.closest
        ? event.target.closest(
            '[data-nr-booth-v2-enter], [data-nr-booth-v2-capture], [data-nr-booth-v2-download], [data-nr-booth-v2-post], [data-nr-booth-v2-more]'
          )
        : null;
      if (!target || !root.contains(target)) return;
      var name = boothButtonName(target);
      if (name) trackBooth(name);
    });
    window.addEventListener('pagehide', stop);
    window.addEventListener('beforeunload', stop);
    document.addEventListener('shopify:section:unload', function (event) {
      if (event.target && event.target.contains(root)) stop();
    });

    return { stop: stop };
  }

  function initAll() {
    document.querySelectorAll('[data-nr-photo-booth-v2]').forEach(function (root) {
      if (root.__nrPhotoBoothV2) return;
      root.__nrPhotoBoothV2 = createBooth(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
